import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BingoAnimationProps {
  type: 'daily' | 'weekly';
  onComplete?: () => void;
}

const BingoAnimation: React.FC<BingoAnimationProps> = ({ type, onComplete }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [confetti, setConfetti] = useState<Array<{ id: number; left: number; delay: number }>>([]);

  useEffect(() => {
    // Generate confetti pieces
    const pieces = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2
    }));
    setConfetti(pieces);

    // Auto-hide after animation
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
      {/* Confetti */}
      {confetti.map((piece) => (
        <div
          key={piece.id}
          className="absolute w-2 h-2 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-confetti"
          style={{
            left: `${piece.left}%`,
            animationDelay: `${piece.delay}s`,
            animationDuration: '3s'
          }}
        />
      ))}

      {/* Main animation container */}
      <div className="relative">
        {/* Background glow */}
        <div className={`absolute inset-0 rounded-full blur-xl ${
          type === 'weekly'
            ? 'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500'
            : 'bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500'
        } animate-pulse opacity-50 scale-150`} />

        {/* Main content */}
        <div className={`relative bg-white rounded-3xl shadow-2xl p-8 text-center transform animate-bounce-in ${
          type === 'weekly' ? 'border-4 border-yellow-400' : 'border-4 border-blue-400'
        }`}>
          {/* Trophy/Cup for weekly, Star for daily */}
          <div className="mb-6">
            {type === 'weekly' ? (
              <div className="text-8xl animate-spin-slow">🏆</div>
            ) : (
              <div className="text-8xl animate-bounce">⭐</div>
            )}
          </div>

          {/* Title */}
          <h2 className={`text-3xl font-bold mb-4 ${
            type === 'weekly' ? 'text-yellow-600' : 'text-blue-600'
          }`}>
            {type === 'weekly' ? t('animations.weeklyBingo') : t('animations.dailyBingo')}
          </h2>

          {/* Message */}
          <p className="text-lg text-gray-700 mb-6">
            {type === 'weekly' ? t('animations.weeklyBingoMessage') : t('animations.dailyBingoMessage')}
          </p>

          {/* Spark points indicator */}
          <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${
            type === 'weekly' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
          }`}>
            <span className="text-2xl">✨</span>
            <span className="font-semibold">
              +{type === 'weekly' ? '75' : '5'} {t('animations.sparkPoints')}
            </span>
          </div>
        </div>

        {/* Floating sparkles */}
        <div className="absolute -top-4 -right-4 text-4xl animate-float">✨</div>
        <div className="absolute -bottom-4 -left-4 text-3xl animate-float-delayed">⭐</div>
        <div className="absolute top-1/2 -right-8 text-2xl animate-float">💫</div>
      </div>
    </div>
  );
};

export default BingoAnimation;
